import { QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import { Passage } from 'pagesNew/PracticeTests/Form/PassagesFieldArray'
import { isPassageQuestion } from 'pagesNew/Questions/Form/questionTypeConfig'

type PassageFieldError = Record<string, string>
type PassageErrors = Array<PassageFieldError | undefined> & { _error?: string }

export type PassageSectionRange = {
  start: number
  end: number
  label: string
}

export type PassagesError = PassageErrors | { _error: string }

const getRange = (passage: Passage) => {
  const start = Number(passage.startQuestion)
  const end = Number(passage.endQuestion)

  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return null
  }

  return { start, end }
}

const setError = (errors: Array<PassageFieldError | undefined>, index: number, field: string, message: string) => {
  if (errors[index]?.[field]) {
    return
  }

  errors[index] = {
    ...errors[index],
    [field]: message,
  }
}

const getSectionError = (start: number, end: number, sections: PassageSectionRange[]) => {
  const startSection = sections.find((section) => start >= section.start && start <= section.end)
  const endSection = sections.find((section) => end >= section.start && end <= section.end)

  if (startSection && endSection && startSection === endSection) {
    return
  }

  if (startSection && endSection) {
    return `Questions must belong to the same section (${startSection.label} and ${endSection.label})`
  }

  return 'Questions must belong to the same section'
}

export const validatePassages = (
  passages: Passage[] | undefined,
  questions: Array<{ type: QuestionTypeEnum }> | undefined,
  sections?: PassageSectionRange[]
): PassagesError | undefined => {
  const questionsCount = questions?.length ?? 0
  const passageQuestionIndices = new Set(
    (questions ?? []).flatMap((question, index) => (isPassageQuestion(question.type) ? [index + 1] : []))
  )
  const passagesValue = passages ?? []

  if (!passageQuestionIndices.size) {
    if (!passagesValue.length) {
      return
    }

    return passagesValue.map(() => ({
      startQuestion: 'There are no split-screen questions to link',
    }))
  }

  if (!passagesValue.length) {
    return { _error: 'Link all split-screen questions to passages' }
  }

  const errors: PassageErrors = []
  const covered = new Set<number>()
  const validRanges: Array<{ index: number; start: number; end: number }> = []
  const allRangesFilled = passagesValue.every((passage) => getRange(passage))

  passagesValue.forEach((passage, index) => {
    const range = getRange(passage)

    if (!range) {
      return
    }

    const { start, end } = range

    if (start > end) {
      setError(errors, index, 'startQuestion', 'Should not be more than End Question')
      return
    }

    if (start < 1 || start > questionsCount) {
      setError(errors, index, 'startQuestion', `Should be between 1 and ${questionsCount}`)
    }

    if (end < 1 || end > questionsCount) {
      setError(errors, index, 'endQuestion', `Should be between 1 and ${questionsCount}`)
    }

    if (start < 1 || end > questionsCount || start > questionsCount || end < 1) {
      return
    }

    if (sections?.length) {
      const sectionError = getSectionError(start, end, sections)

      if (sectionError) {
        setError(errors, index, 'startQuestion', sectionError)
        return
      }
    }

    for (let questionIndex = start; questionIndex <= end; questionIndex += 1) {
      if (!passageQuestionIndices.has(questionIndex)) {
        setError(errors, index, 'startQuestion', 'Should include only split-screen questions')
        return
      }
    }

    const overlapping = validRanges.find((existing) => start <= existing.end && end >= existing.start)

    if (overlapping) {
      setError(
        errors,
        index,
        'startQuestion',
        `Should be no common questions for Passages #${overlapping.index + 1} and #${index + 1}`
      )
      return
    }

    validRanges.push({ index, start, end })

    for (let questionIndex = start; questionIndex <= end; questionIndex += 1) {
      covered.add(questionIndex)
    }
  })

  const hasUncoveredPassageQuestions = Array.from(passageQuestionIndices).some(
    (questionIndex) => !covered.has(questionIndex)
  )

  if (!allRangesFilled) {
    errors._error = 'All passages should include questions'
  } else if (errors.length) {
    errors._error = 'Some passages has incorrect questions range'
  } else if (hasUncoveredPassageQuestions) {
    errors._error = 'All split-screen questions must be linked to passages'
  }

  if (errors.some(Boolean) || errors._error) {
    return errors
  }
}
